import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const STORAGE_KEYS = {
  SESSION_TOKEN: 'authToken',
  EATZY_ACCESS_TOKEN: 'eatzy_access_token',
  EATZY_REFRESH_TOKEN: 'eatzy_refresh_token',
  EATZY_TOKEN_EXPIRES: 'eatzy_token_expires',
};

export const INITIAL_STATE = {
  sessionToken: null as string | null,
  eatzyAccessToken: null as string | null,
  eatzyRefreshToken: null as string | null,
  eatzyTokenExpires: null as number | null,
  authUser: null as any,
  isLoading: false,
  error: null as any,
  isSuccess: false,

  accessToken: null as any,
  refreshToken: null as any,

  isSilentAccount: false as boolean,
  guestContactEmail: null as string | null,
  silentAccountPassword: null as string | null,
};

const syncToLocalStorage = (key: string, value: string | null) => {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    // localStorage may be unavailable
  }
};

const authorizationSlice = createSlice({
  name: 'authorization',
  initialState: { ...INITIAL_STATE },
  reducers: {
    resetAll() {
      syncToLocalStorage(STORAGE_KEYS.SESSION_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_ACCESS_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_REFRESH_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_TOKEN_EXPIRES, null);
      return { ...INITIAL_STATE };
    },
    resetAuthorization(state) {
      return {
        ...state,
        isLoading: false,
        isSuccess: false,
      };
    },
    authorizeUser: (state) => state,
    registerUser: (state) => state,
    forgotPasswordRequest: (state) => state,
    resetPasswordRequest: (state) => state,
    setSessionToken(state, { payload }) {
      syncToLocalStorage(STORAGE_KEYS.SESSION_TOKEN, payload);
      return {
        ...state,
        sessionToken: payload,
      };
    },
    setEatzyTokens(state, { payload }) {
      const { accessToken, refreshToken, accessExpires } = payload || {};
      const k = STORAGE_KEYS;
      syncToLocalStorage(k.EATZY_ACCESS_TOKEN, accessToken || null);
      syncToLocalStorage(k.EATZY_REFRESH_TOKEN, refreshToken || null);
      syncToLocalStorage(
        k.EATZY_TOKEN_EXPIRES,
        accessExpires ? String(accessExpires) : null
      );
      return {
        ...state,
        eatzyAccessToken: accessToken || null,
        eatzyRefreshToken: refreshToken || null,
        eatzyTokenExpires: accessExpires || null,
      };
    },
    setAuthUser(state, { payload }) {
      return {
        ...state,
        authUser: payload,
      };
    },
    clearAuth() {
      syncToLocalStorage(STORAGE_KEYS.SESSION_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_ACCESS_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_REFRESH_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_TOKEN_EXPIRES, null);
      return { ...INITIAL_STATE };
    },
    setAccessToken(state, { payload }) {
      if (payload && payload.token) {
        syncToLocalStorage(STORAGE_KEYS.EATZY_ACCESS_TOKEN, payload.token);
      }
      return {
        ...state,
        accessToken: payload,
        eatzyAccessToken: payload?.token || state.eatzyAccessToken,
      };
    },
    setRefreshToken(state, { payload }) {
      if (payload && payload.token) {
        syncToLocalStorage(STORAGE_KEYS.EATZY_REFRESH_TOKEN, payload.token);
      }
      return {
        ...state,
        refreshToken: payload,
        eatzyRefreshToken: payload?.token || state.eatzyRefreshToken,
      };
    },
    updateLoadingStatus(state, { payload }) {
      return {
        ...state,
        isLoading: payload,
      };
    },
    updateError(state, { payload }) {
      return {
        ...state,
        error: payload,
      };
    },
    updateSuccessStatus(state, { payload }) {
      return {
        ...state,
        isSuccess: payload,
      };
    },
    setSilentAccountState(state, { payload }) {
      return {
        ...state,
        isSilentAccount: payload.isSilentAccount,
        guestContactEmail: payload.guestContactEmail,
        silentAccountPassword: payload.silentAccountPassword,
      };
    },
    clearSilentAccountState(state) {
      return {
        ...state,
        isSilentAccount: false,
        guestContactEmail: null,
        silentAccountPassword: null,
      };
    },
    silentRegister: (state) => state,
    setSilentAccount(
      state,
      { payload }: PayloadAction<{ contactEmail: string; password: string }>
    ) {
      return {
        ...state,
        isSilentAccount: true,
        guestContactEmail: payload.contactEmail,
        silentAccountPassword: payload.password,
      };
    },
    clearSilentAccount(state) {
      return {
        ...state,
        isSilentAccount: false,
        guestContactEmail: null,
        silentAccountPassword: null,
      };
    },
    upgradeSilentAccount: (state) => state,
    logoutRequest: (state) => state,
    logoutResponse: () => {
      syncToLocalStorage(STORAGE_KEYS.SESSION_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_ACCESS_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_REFRESH_TOKEN, null);
      syncToLocalStorage(STORAGE_KEYS.EATZY_TOKEN_EXPIRES, null);
      return INITIAL_STATE;
    },
    restoreSession: (state) => state,
  },
});

export const {
  setAccessToken,
  setRefreshToken,
  resetAuthorization,
  authorizeUser,
  registerUser,
  forgotPasswordRequest,
  resetPasswordRequest,
  updateLoadingStatus,
  updateError,
  updateSuccessStatus,
  setSilentAccountState,
  clearSilentAccountState,
  silentRegister,
  logoutRequest,
  logoutResponse,
  resetAll,
  setSessionToken,
  setEatzyTokens,
  setAuthUser,
  clearAuth,
  restoreSession,
  setSilentAccount,
  clearSilentAccount,
  upgradeSilentAccount,
} = authorizationSlice.actions;

export default authorizationSlice.reducer;
